import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { Calendar, Download, ExternalLink, FileText, Mail, PlayCircle, Printer, ShieldCheck } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Card, CardContent } from '../components/ui/card';

const OPEN_WEIGHTS_PUBLIC_PDF = 'https://res.cloudinary.com/dp5hfqkdu/raw/upload/v1784952332/SALAR_CBAE_Open_Weight_Public_Safe_White_Paper_v1_1_8pp.pdf';
const OPEN_WEIGHTS_HERO = 'https://res.cloudinary.com/dp5hfqkdu/image/upload/v1784951478/open-weights-governable-execution-2026.webp';

const papers = [
  {
    "id": "choosing-ai-that-lasts",
    "visual": "ai-contest",
    "number": "SALAR-AI-2026-10 · v3.0",
    "title": "Choosing AI That Lasts",
    "date": "October 26, 2026",
    "format": "15-page public white paper · 156-page full technical study",
    "author": "Vikrant Salar, Founder & Principal, SALAR Technologies LLC",
    "href": "/documents/SALAR_Choosing_AI_That_Lasts_Public_White_Paper_15pp.pdf",
    "primaryLabel": "Download 15-Page White Paper",
    "secondaryHref": "/ai-in-contest.html#request-study",
    "secondaryLabel": "Request the Full 156-Page Study",
    "diagramHref": "/images/ai-in-contest/architecture.svg",
    "subtitle": "Global models, measured capability, cost, risk and survival, 2026–2031.",
    "summary": "Compare named published coding, factuality, grounding and tool-use results, then look beyond the US through a 46-entry regional atlas. Edition 3 connects accepted outcomes with full cost, security, measured hardware throughput, physical constraints and continuity. The 156-page draft includes 34 figures, 55 equations, 183 sources and six worked cases; the public extract selects nine figures. No new proprietary-model tournament was run by SALAR.",
    "requirementsLabel": "Questions the study answers",
    "requirements": [
        "Choose appropriate AI configurations for research, coding, documents, numerical work and local deployment.",
        "Compare human review and total cost per accepted task alongside model capability.",
        "Understand trust boundaries, patch timing, correlated failures and tested recovery.",
        "Evaluate conditional survival pathways for platforms, frontier services, open-weight ecosystems and specialist applications."
    ],
    "tags": [
        "AI selection",
        "Cyber resilience",
        "Task economics",
        "Hardware",
        "Survival outlook",
        "2031 scenarios"
    ]
},
  {
    id: 'ai-sovereignty-model-centralization',
    visual: 'sovereignty',
    number: 'SALAR-WP-2026-006 v1.0',
    title: 'AI Sovereignty and Model Centralization',
    date: 'August 29, 2026',
    format: '3-page public executive brief • 26-page controlled report',
    author: 'Vikrant Salar, Founder & Principal, SALAR Technologies LLC',
    href: '/documents/SALAR_AI_Sovereignty_Executive_Brief_WP_2026_006.pdf',
    primaryLabel: 'Download 3-Page Executive Brief',
    secondaryHref: '/ai-sovereignty-report',
    secondaryLabel: 'Request / Unlock Full Report',
    subtitle: 'Who controls AI—and who controls the semiconductor stack beneath it?',
    summary: 'This paper answers two questions: who leads the AI race, and which physical dependencies can cap that lead? It assesses models, accelerator architecture, HBM, foundry and packaging capacity, cloud infrastructure, energy and policy. The conclusion: the United States leads, China challenges, Europe shapes rules, India is the wildcard, and Taiwan-centered advanced fabrication and packaging remain the decisive physical constraint.',
    requirementsLabel: 'What the paper establishes',
    requirements: [
      'Map AI leadership across models, capital, compute, talent, regulation, and semiconductor chokepoints.',
      'Distinguish accelerator design, EUV lithography, leading-edge foundry, HBM, packaging, cloud, and power.',
      'Use the 55% / 35% / 10% 2031 scenario frame as structured judgment—not measured probability.',
      'Define sovereignty as controlled dependence with credible, tested exit options.',
    ],
    tags: ['AI sovereignty', 'Semiconductors', 'Geopolitics', 'AI infrastructure', 'Taiwan', '2031 scenarios'],
    disclaimer: 'Independent strategic research. Scenario percentages are analytical judgments, not measured probabilities, forecasts, investment advice, certifications, or government or company endorsements.',
  },
  {
    id: 'open-weights-governable-execution',
    visual: 'openweights',
    number: 'SALAR-WP-2026-005 v1.1',
    title: 'Open Weights, Governable Execution',
    date: 'July 24, 2026',
    format: '8-page public-safe technical brief',
    author: 'Vikrant Salar, Founder & Principal, SALAR Technologies LLC',
    href: OPEN_WEIGHTS_PUBLIC_PDF,
    primaryLabel: 'Download 8-Page Public Brief',
    diagramHref: '/img/diagrams/cbae-open-weight-general-block-diagram.svg',
    subtitle: 'Capability-bounded deployment for sovereign AI.',
    summary: 'A concise public engineering treatment of the deployment boundary where open-weight model sovereignty meets independently governed autonomous execution. The brief defines requirements for artifact binding, short-lived workload identity, signed capability leases, default-deny egress, tool mediation, hard resource and transaction ceilings, fresh finality checks, effect-bound execution receipts, replay resistance, and out-of-band termination. It also includes public-safe mathematical models, worked examples, and a falsifiable conformance framework.',
    requirements: [
      'Bind the approved model, runtime image, manifest, and attestation result to one short-lived workload identity.',
      'Authorize tools, actions, objects, destinations, data scopes, validity windows, and budgets through signed capability leases.',
      'Enforce egress, quotas, policy, finality, receipts, and emergency revocation outside the model and agent workload.',
      'Generate parent-linked, effect-bound receipts that preserve freshness, revocation context, policy version, and observed results.',
      'Measure residual authority exposure, evidence-adjusted health, unauthorized-effect probability, and complete emergency-stop latency.',
    ],
    tags: ['Open weights', 'Agentic AI', 'Capability leases', 'Execution receipts', 'Fresh finality', 'Emergency stop', 'Risk mathematics'],
    disclaimer: 'Independent public engineering work. SALAR Technologies LLC is not affiliated with, sponsored by, or endorsed by OpenAI, Hugging Face, Microsoft, or any standards organization. Incident references reflect public disclosures. All quantitative examples are synthetic engineering scenarios, not observed incident frequencies, certifications, or vendor-performance claims.',
  },
  {
    id: 'capability-bounded-autonomous-execution',
    visual: 'cbae',
    number: 'SALAR-WP-2026-004 v2.0',
    title: 'Capability-Bounded Autonomous Execution',
    date: 'July 22, 2026',
    format: 'Zenodo technical report • Version 2.0',
    author: 'Vikrant Salar, Founder & Principal, SALAR Technologies LLC',
    href: 'https://zenodo.org/records/21519502',
    doiHref: 'https://doi.org/10.5281/zenodo.21519502',
    doi: '10.5281/zenodo.21519502',
    allVersionsDoi: '10.5281/zenodo.21519501',
    rfcHref: 'https://github.com/cosai-oasis/ws4-secure-design-agentic-systems/issues/146',
    prototypeHref: '/agent-containment-control-cell',
    checksum: '542ccc08f66f221e499b46675bec804121b68c96442cfed38248c0f98dd4e586',
    subtitle: 'Independent authority, containment, and evidence controls for autonomous agents.',
    summary: 'A public engineering treatment of Capability-Bounded Autonomous Execution (CBAE): explicit authority dimensions, signed short-lived capability leases, use-time verification, non-self-modifiable budgets, positive-expansion authorization, out-of-band termination, tamper-evident evidence, and finality gates for consequential external effects. Version 2.0 is permanently published on Zenodo and the related secure-design proposal is under community review as CoSAI RFC #146.',
    tags: ['Agentic AI', 'Capability leases', 'Containment', 'Evidence controls', 'Finality'],
  },
  {
    id: 'token-economy-whitepaper',
    visual: 'token',
    number: 'SALAR-WP-2026-003 v1.4',
    title: 'The Token Economy',
    date: 'July 3, 2026',
    format: 'Technical white paper PDF',
    author: 'Vikrant Salar, Founder & Principal, SALAR Technologies LLC',
    href: '/documents/SALAR_Token_Economy_Whitepaper.pdf',
    subtitle: 'How hosted intelligence becomes metered infrastructure cost.',
    summary: 'A public technical analysis of token billing, inference work, pricing transparency, and the trust problem created when usage is hard for customers to see before the bill arrives.',
    tags: ['Token economics', 'Inference', 'AI pricing', 'Transparency'],
  },
  {
    id: 'memory-as-the-new-bottleneck',
    visual: 'memory',
    number: 'Whitepaper 001',
    title: 'Memory as the New Bottleneck',
    date: 'June 2026',
    format: 'Technical research note',
    author: 'SALAR Technologies LLC / Vikrant Salar, Founder & Principal',
    href: null,
    subtitle: 'Memory and storage R&D for AI across data center, telecom, and edge systems.',
    summary: 'A public technical note on why modern inference is increasingly constrained by bandwidth, capacity, KV-cache growth, power, packaging, and supply assurance rather than arithmetic alone.',
    tags: ['Memory-bound inference', 'HBM', 'DRAM', 'CXL', 'Edge AI'],
  },
];

const cbaeFindings = [
  ['Authority must be explicit', 'Represent network, identity, privilege, tools, data, secrets, compute, and irreversible-action scope as a signed capability envelope.'],
  ['Expansion is a new decision', 'An agent cannot extend its own allowlist, mint credentials, raise budgets, or weaken the stopping rule.'],
  ['Termination must be independent', 'The control plane can revoke identity, cut egress, stop execution, and preserve evidence without cooperation from the agent.'],
  ['Containment must be testable', 'Measure escape rate, credential lifetime, kill latency, budget enforcement, evidence completeness, freshness, revocation context, and containment margins.'],
];

const tokenFindings = [
  ['Tokens meter real work', 'Each generated token uses compute, memory movement, power, and infrastructure capacity.'],
  ['The issue is opacity', 'Customers need visible request-level usage, not surprise consumption after the bill arrives.'],
  ['Cost curves are moving', 'Buyers should separate real compute cost from unclear premium pricing.'],
  ['Govern the meter', 'Usage exports, hard budget controls, and clear change notices should become normal.'],
];

const quantumPoints = [
  ['Quantum is not magic', 'It is probability, measurement, noise, hardware limits, and engineering trade space.'],
  ['AI raises the stakes', 'Future systems need acceleration, trust, and physics-aware infrastructure decisions.'],
  ['Semiconductors remain the bridge', 'Quantum, AI, and token economics meet in devices, packaging, memory, power, and provenance.'],
];

const memoryPriorities = [
  ['Workload-aware co-design', 'Use real traces to map token workloads to memory tiers and service-level constraints.'],
  ['Tiering and disaggregation', 'Place HBM, DRAM, CXL, and NAND by access pattern, latency, power, and trust requirement.'],
  ['Power and token economics', 'Optimize joules and dollars per token, not benchmark throughput alone.'],
  ['Reliability and provenance', 'Govern memory, board, package, firmware, and supplier evidence through the service life.'],
];

function VisualPanel({ type }) {
  if (type === 'ai-contest') {
    return (
      <figure className="overflow-hidden rounded-xl border border-stone-300 bg-white text-slate-800">
        <img src="/images/ai-in-contest/engineering-research.jpg" alt="NIST engineer Richard Kasica holding a silicon wafer beside fabrication equipment" className="block h-auto w-full" loading="eager" />
        <figcaption className="p-3 text-xs leading-relaxed text-slate-600">Real semiconductor engineering. Photo: Curt Suplee/NIST. <a href="https://www.nist.gov/image/making-computer-chips" target="_blank" rel="noopener noreferrer" className="underline">Source and credit</a>.</figcaption>
      </figure>
    );
  }

  if (type === 'sovereignty') {
    return (
      <div className="overflow-hidden rounded-2xl border border-amber-300/35 bg-[#07101f] shadow-2xl shadow-cyan-950/30">
        <img
          src="/img/news/salar-ai-sovereignty-map-2026.webp"
          alt="SALAR AI Dominance Map 2026 showing the strategic roles of the United States, China, Europe, India, Russia, and Taiwan"
          className="block h-auto w-full object-contain"
          loading="eager"
        />
      </div>
    );
  }

  if (type === 'openweights') {
    return (
      <div className="overflow-hidden rounded-2xl border border-cyan-400/30 bg-[#07101f] shadow-2xl shadow-cyan-950/30">
        <img
          src={OPEN_WEIGHTS_HERO}
          alt="Realistic sovereign-AI accelerator and external authority shield representing capability-bounded autonomous execution"
          className="block aspect-video w-full object-cover"
          loading="eager"
        />
      </div>
    );
  }

  if (type === 'cbae') {
    return (
      <div className="overflow-hidden rounded-2xl border border-cyan-400/30 bg-[#07101f] shadow-2xl shadow-cyan-950/30">
        <img
          src="/img/news/cbae-publication-v2-realistic.webp"
          alt="Cinematic CBAE shield representing independently enforced authority, containment, and evidence controls"
          className="block aspect-video w-full object-cover"
          loading="lazy"
        />
      </div>
    );
  }

  if (type === 'token') {
    return (
      <div className="overflow-hidden rounded-2xl border border-cyan-400/30 bg-[#07101f] shadow-2xl shadow-cyan-950/30">
        <img
          src="/img/news/token-economy-realistic.webp"
          alt="Cinematic Token Economy research scene with a SALAR token, analytics chart, and technical report"
          className="block aspect-video w-full object-cover"
          loading="lazy"
        />
      </div>
    );
  }

  const labels = ['HBM', 'DRAM', 'CXL', 'NAND'];
  return (
    <div className="rounded-2xl border border-cyan-400/30 bg-[#07101f] p-5 shadow-inner">
      <div className="mb-4 text-xs font-bold uppercase tracking-widest text-cyan-300">SALAR Visual Brief</div>
      <h3 className="mb-2 text-2xl font-extrabold text-white">Memory Bottleneck Map</h3>
      <p className="mb-6 text-sm text-blue-100">bandwidth • latency • power • trust</p>
      <div className="grid grid-cols-2 gap-3">
        {labels.map((label, index) => (
          <div key={label} className="rounded-xl border border-blue-400/30 bg-blue-950/50 p-4 text-center">
            <div className="mx-auto mb-3 flex h-10 w-10 items-center justify-center rounded-full border border-cyan-300/60 bg-cyan-300/10 text-xs font-black text-cyan-200">{index + 1}</div>
            <div className="text-sm font-bold text-white">{label}</div>
          </div>
        ))}
      </div>
      <div className="mt-6 rounded-xl border border-cyan-300/30 bg-cyan-300/10 p-4 text-center text-lg font-black text-white">Memory movement sets the limit</div>
    </div>
  );
}

function PointCard({ title, body, index }) {
  return (
    <div className="flex gap-4">
      <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-cyan-400/50 bg-cyan-500/20 font-bold text-cyan-200">{index + 1}</div>
      <div>
        <h3 className="font-bold text-white">{title}</h3>
        <p className="text-sm leading-relaxed text-blue-100">{body}</p>
      </div>
    </div>
  );
}

function WhitepaperCard({ paper, featured }) {
  const requestSubject = encodeURIComponent(`Request SALAR whitepaper PDF - ${paper.title}`);
  return (
    <Card id={paper.id} className={`scroll-mt-28 overflow-hidden border-cyan-500/30 bg-[#0F1A2B]/95 text-white ${featured ? 'shadow-2xl shadow-cyan-950/40 ring-1 ring-cyan-400/20' : ''}`}>
      <div className="grid gap-0 lg:grid-cols-[0.9fr_1.1fr]">
        <div className="flex items-center bg-[#06101f] p-5 md:p-7"><VisualPanel type={paper.visual} /></div>
        <CardContent className="p-7 md:p-8">
          <div className="mb-4 flex flex-wrap items-center gap-3">
            <Badge variant="outline" className="border-cyan-400/60 text-[11px] tracking-widest text-cyan-300">{paper.number}</Badge>
            <div className="flex items-center text-xs text-blue-200"><Calendar size={13} className="mr-1.5" />{paper.date}</div>
            {featured && <Badge className="border border-emerald-400/40 bg-emerald-400/15 text-emerald-200">Recent Release</Badge>}
          </div>
          <h2 className="mb-3 text-3xl font-bold text-white">{paper.title}</h2>
          <p className="mb-4 font-semibold leading-relaxed text-cyan-200">{paper.subtitle}</p>
          <p className="mb-6 leading-relaxed text-blue-100">{paper.summary}</p>
          <div className="mb-6 grid grid-cols-1 gap-3 text-sm text-blue-100 sm:grid-cols-2">
            <div className="rounded-lg border border-blue-500/20 bg-blue-950/50 p-3"><span className="font-semibold text-cyan-300">Format:</span><br />{paper.format}</div>
            <div className="rounded-lg border border-blue-500/20 bg-blue-950/50 p-3"><span className="font-semibold text-cyan-300">Author:</span><br />{paper.author}</div>
          </div>
          {paper.requirements && (
            <div className="mb-6 rounded-xl border border-cyan-400/25 bg-cyan-400/10 p-5">
              <div className="mb-3 text-sm font-bold uppercase tracking-widest text-cyan-200">{paper.requirementsLabel || 'Implementation requirements covered'}</div>
              <ul className="space-y-2 text-sm leading-relaxed text-blue-100">
                {paper.requirements.map((item) => <li key={item} className="flex gap-2"><span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-cyan-300" />{item}</li>)}
              </ul>
            </div>
          )}
          <div className="mb-6 flex flex-wrap gap-2">{paper.tags.map((tag) => <span key={tag} className="rounded-full border border-cyan-300/20 bg-cyan-300/10 px-3 py-1 text-xs font-semibold text-cyan-100">{tag}</span>)}</div>
          <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap">
            {paper.href ? (
              <Button asChild className="bg-cyan-500 font-bold text-[#07101f] hover:bg-cyan-400"><a href={paper.href} target="_blank" rel="noopener noreferrer"><Download className="mr-2" size={18} />{paper.primaryLabel || (paper.doi ? 'Open Zenodo Record' : 'Public Copy')}</a></Button>
            ) : (
              <Button asChild variant="outline" className="border-cyan-400 text-cyan-200 hover:bg-cyan-500/10"><a href={`mailto:contact@salartechnologies.com?subject=${requestSubject}`}><Mail className="mr-2" size={18} />Request PDF Copy</a></Button>
            )}
            {paper.secondaryHref && <Button asChild variant="outline" className="border-emerald-400/60 text-emerald-200 hover:bg-emerald-500/10"><a href={paper.secondaryHref} target="_blank" rel="noopener noreferrer"><FileText className="mr-2" size={18} />{paper.secondaryLabel}</a></Button>}
            {paper.diagramHref && <Button asChild variant="outline" className="border-blue-400/60 text-blue-100 hover:bg-blue-500/10"><a href={paper.diagramHref} target="_blank" rel="noopener noreferrer"><ExternalLink className="mr-2" size={18} />Open General Block Diagram</a></Button>}
            {paper.doiHref && <Button asChild variant="outline" className="border-cyan-400/60 text-cyan-200 hover:bg-cyan-500/10"><a href={paper.doiHref} target="_blank" rel="noopener noreferrer"><Download className="mr-2" size={18} />DOI / Citation</a></Button>}
            {paper.rfcHref && <Button asChild variant="outline" className="border-violet-400/60 text-violet-200 hover:bg-violet-500/10"><a href={paper.rfcHref} target="_blank" rel="noopener noreferrer"><ExternalLink className="mr-2" size={18} />CoSAI RFC #146</a></Button>}
            {paper.prototypeHref && <Button asChild variant="outline" className="border-emerald-400/60 text-emerald-200 hover:bg-emerald-500/10"><Link to={paper.prototypeHref}><PlayCircle className="mr-2" size={18} />Open Control Cell</Link></Button>}
            <Button onClick={() => window.print()} variant="outline" className="border-blue-400/50 text-blue-100 hover:bg-blue-500/10"><Printer className="mr-2" size={18} />Print / Save Page</Button>
          </div>
          {paper.visual === 'cbae' && (
            <div className="mt-5 space-y-3">
              <div className="rounded-lg border border-emerald-400/25 bg-emerald-400/10 p-4 text-xs leading-relaxed text-emerald-100">
                <div className="font-bold text-emerald-200">Permanent publication record</div>
                <div className="mt-1">Version DOI: <a className="underline decoration-emerald-300/50 underline-offset-2" href={paper.doiHref} target="_blank" rel="noopener noreferrer">{paper.doi}</a></div>
                <div>All-versions DOI: https://doi.org/{paper.allVersionsDoi}</div>
              </div>
              <p className="rounded-lg border border-blue-400/20 bg-blue-950/50 p-4 text-xs leading-relaxed text-blue-200">Independent public-source analysis. SALAR Technologies LLC is not affiliated with, sponsored by, or endorsed by OpenAI or Hugging Face. Incident descriptions are based on preliminary public disclosures available as of July 22, 2026.</p>
              <p className="break-all font-mono text-[10px] leading-relaxed text-blue-400">SHA-256: {paper.checksum}</p>
            </div>
          )}
          {paper.disclaimer && <p className="mt-5 rounded-lg border border-blue-400/20 bg-blue-950/50 p-4 text-xs leading-relaxed text-blue-200">{paper.disclaimer}</p>}
        </CardContent>
      </div>
    </Card>
  );
}

export default function Whitepapers() {
  return (
    <div className="min-h-screen bg-[#0A1628] text-white" data-testid="whitepapers-page">
      <Helmet>
        <title>Whitepapers | SALAR Technologies Technical Research</title>
        <meta name="description" content="SALAR Technologies public engineering white papers on open-weight AI, capability-bounded autonomous execution, independent authority, finality, evidence controls, AI infrastructure economics, and memory bottlenecks." />
        <link rel="canonical" href="https://www.salartechnologies.com/whitepapers" />
        <meta name="robots" content="index, follow" />
      </Helmet>
      <section className="relative overflow-hidden border-b border-blue-800/30 py-20">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_28%_18%,rgba(0,212,255,0.14),transparent_34%),linear-gradient(135deg,#0A1628_0%,#0F1A2B_56%,#102D52_100%)]" />
        <div className="container relative z-10 mx-auto px-6">
          <div className="mx-auto max-w-5xl text-center">
            <Badge variant="outline" className="mb-5 border-cyan-400 text-xs tracking-widest text-cyan-300">TECHNICAL WHITE PAPER SERIES</Badge>
            <h1 className="mb-6 text-4xl font-bold lg:text-6xl">SALAR White Papers</h1>
            <p className="mx-auto max-w-3xl text-xl leading-relaxed text-blue-100">The newest SALAR publication appears first. Earlier white papers remain available below in reverse-chronological order.</p>
          </div>
        </div>
      </section>
      <section className="py-16"><div className="container mx-auto max-w-7xl space-y-8 px-6">{papers.map((paper, index) => <WhitepaperCard key={paper.id} paper={paper} featured={index === 0} />)}</div></section>
      <section className="border-y border-blue-800/30 bg-white/5 py-16"><div className="container mx-auto max-w-6xl px-6"><div className="mb-8"><Badge variant="outline" className="mb-3 border-cyan-400 text-cyan-300">WHAT THE CBAE PAPERS ESTABLISH</Badge><h2 className="text-3xl font-bold text-white">Containment as an independently enforced systems property</h2></div><div className="grid grid-cols-1 gap-5 md:grid-cols-2">{cbaeFindings.map(([title, body], index) => <Card key={title} className="border-cyan-500/20 bg-[#0F1A2B]/90 text-white"><CardContent className="p-6"><PointCard title={title} body={body} index={index} /></CardContent></Card>)}</div></div></section>
      <section className="py-16"><div className="container mx-auto max-w-6xl px-6"><div className="mb-8"><Badge variant="outline" className="mb-3 border-cyan-400 text-cyan-300">TOKEN ECONOMY RESEARCH</Badge><h2 className="text-3xl font-bold text-white">A cleaner reading of token economics</h2></div><div className="grid grid-cols-1 gap-5 md:grid-cols-2">{tokenFindings.map(([title, body], index) => <Card key={title} className="border-cyan-500/20 bg-[#0F1A2B]/90 text-white"><CardContent className="p-6"><PointCard title={title} body={body} index={index} /></CardContent></Card>)}</div></div></section>
      <section className="border-t border-blue-800/30 bg-white/5 py-16"><div className="container mx-auto max-w-6xl px-6"><div className="grid grid-cols-1 gap-8 lg:grid-cols-2"><Card className="border-blue-500/30 bg-[#0F1A2B]/90 text-white"><CardContent className="p-7"><h2 className="mb-5 flex items-center text-2xl font-bold text-white"><FileText className="mr-3 text-cyan-300" />Quantum computing context</h2><div className="space-y-4">{quantumPoints.map(([title, body], index) => <PointCard key={title} title={title} body={body} index={index} />)}</div></CardContent></Card><Card className="border-blue-500/30 bg-[#0F1A2B]/90 text-white"><CardContent className="p-7"><h2 className="mb-5 flex items-center text-2xl font-bold text-white"><ShieldCheck className="mr-3 text-cyan-300" />Memory-bottleneck priorities retained</h2><div className="space-y-4">{memoryPriorities.map(([title, body], index) => <PointCard key={title} title={title} body={body} index={index} />)}</div></CardContent></Card></div></div></section>
    </div>
  );
}

